import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Badge,
  Spinner,
  Alert,
  Table,
  Button,
  Row,
  Col,
  InputGroup,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  IconUsers,
  IconSearch,
  IconFilter,
  IconEye,
  IconEdit,
  IconAlertTriangle,
  IconRocket,
  IconUserCheck,
  IconLogout,
  IconTrash,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import SubaccountService from "../../services/SubaccountServices";
import UserContext from "../../contexts/UserContext";
import CreateSubaccountButton from "./components/CreateSubaccountButton";
import EditSubaccountModal from "./modals/EditSubaccountModal";
import AgentImplementationModal from "./modals/AgentImplementationModal";
import OrganizationService from "./services/OrganizationServices";

const OrganizationSubaccountsList = () => {
  const { isOrganization, user, getOrganizationInfo } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [subaccounts, setSubaccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // Estados para modal de edição
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSubaccountId, setSelectedSubaccountId] = useState(null);

  // Estados para modal de implementação
  const [showImplementationModal, setShowImplementationModal] = useState(false);
  const [
    selectedSubaccountForImplementation,
    setSelectedSubaccountForImplementation,
  ] = useState(null);

  // Estados para sessão de acesso ao cliente
  const [activeClientSession, setActiveClientSession] = useState(null);
  const [accessingClient, setAccessingClient] = useState(false);

  useEffect(() => {
    // Verificar se o usuário tem permissão para ver subcontas
    if (isOrganization() && user?.superuser_level <= 2) {
      loadSubaccounts();
    }

    // Verificar se já existe uma sessão ativa de cliente
    checkActiveClientSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, statusFilter]);

  // Verificar sessão ativa de cliente ao carregar
  const checkActiveClientSession = () => {
    const session = OrganizationService.getCurrentSessionInfo();
    setActiveClientSession(session);
  };

  const loadSubaccounts = async () => {
    try {
      setLoading(true);

      const filters = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm,
        status: statusFilter !== "all" ? statusFilter : undefined,
      };

      const params = SubaccountService.buildSearchParams(filters);
      const subaccountsData = await SubaccountService.getSubaccounts(params);

      // Formatar dados para exibição
      const formattedSubaccounts =
        subaccountsData.results?.map((subaccount) =>
          SubaccountService.formatSubaccountData(subaccount)
        ) || [];

      setSubaccounts(formattedSubaccounts);
      setTotalPages(subaccountsData.total_pages || 1);
      setTotalCount(subaccountsData.count || 0);
    } catch (error) {
      console.error("Erro ao carregar subcontas:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar as subcontas.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  // Verificar permissões para diferentes ações
  const canViewSubaccount = (subaccount) => {
    return SubaccountService.canPerformAction(
      "view",
      { isOrganization, user },
      subaccount
    );
  };

  const canEditSubaccount = (subaccount) => {
    return SubaccountService.canPerformAction(
      "edit",
      { isOrganization, user },
      subaccount
    );
  };

  const canDeleteSubaccount = (subaccount) => {
    return SubaccountService.canPerformAction(
      "delete",
      { isOrganization, user },
      subaccount
    );
  };

  const canAccessClientAccount = (subaccount) => {
    return OrganizationService.canAccessClientAccount(user, subaccount);
  };

  // Handlers para ações
  const handleViewSubaccount = (subaccountId) => {
    setSelectedSubaccountId(subaccountId);
    setShowEditModal(true);
  };

  const handleEditSubaccount = (subaccountId) => {
    if (canEditSubaccount()) {
      setSelectedSubaccountId(subaccountId);
      setShowEditModal(true);
    } else {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Apenas donos de organização podem editar subcontas.",
      });
    }
  };

  const handleModalSuccess = () => {
    loadSubaccounts(); // Recarregar lista após alterações
  };

  // Handler para abrir modal de implementação
  const handleImplementAgent = (subaccount) => {
    setSelectedSubaccountForImplementation(subaccount);
    setShowImplementationModal(true);
  };

  // Handler para sucesso da implementação
  const handleImplementationSuccess = () => {
    setShowImplementationModal(false);
    setSelectedSubaccountForImplementation(null);
    // Opcional: mostrar mensagem ou recarregar lista se necessário
  };

  // Handler para deletar subconta
  const handleDeleteSubaccount = async (subaccount) => {
    if (!canDeleteSubaccount(subaccount)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Apenas donos de organização podem deletar subcontas.",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Excluir Subconta Permanentemente",
      html: `
        <div class="text-start">
          <p><strong>Atenção!</strong> Esta ação é <span class="text-danger">irreversível</span>.</p>
          <p>Ao confirmar, todos os dados da subconta <strong>${subaccount.company_name}</strong> serão excluídos permanentemente:</p>
          <ul class="text-muted small">
            <li>Usuários e configurações</li>
            <li>Agentes e automações</li>
            <li>Histórico de conversas</li>
            <li>Todos os dados relacionados</li>
          </ul>
          <p class="mb-0"><strong>Os dados não poderão ser recuperados.</strong></p>
        </div>
      `,
      input: "text",
      inputPlaceholder: "Digite EXCLUIR para confirmar",
      inputValidator: (value) => {
        if (value !== "EXCLUIR") {
          return 'Digite "EXCLUIR" para confirmar a exclusão';
        }
      },
      showCancelButton: true,
      confirmButtonText: "Excluir Permanentemente",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    try {
      const response = await SubaccountService.deleteSubaccount(subaccount.id);

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Subconta Excluída",
          html: `
            <p>${response.message}</p>
            <p class="text-muted small">
              <strong>Empresa:</strong> ${response.deleted_account?.company_name}<br>
              <strong>Email:</strong> ${response.deleted_account?.email}
            </p>
          `,
          confirmButtonColor: "#28a745",
        });

        loadSubaccounts(); // Recarregar lista
      }
    } catch (error) {
      console.error("Erro ao deletar subconta:", error);

      let errorMessage = "Não foi possível excluir a subconta.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      Swal.fire({
        icon: "error",
        title: "Erro ao Excluir",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    }
  };

  // Helper para obter ID da organização de múltiplas fontes
  const getOrganizationId = () => {
    const organizationInfo = getOrganizationInfo();

    // Lista de possíveis fontes do organization_id, em ordem de prioridade
    const possibleSources = [
      organizationInfo?.id,
      user?.organization_id,
      user?.organization?.id,
      user?.account_context?.organization?.id,
      user?.organization_master_id,
    ];

    // Retornar o primeiro ID válido encontrado
    for (const source of possibleSources) {
      if (source && Number.isInteger(Number(source))) {
        return Number(source);
      }
    }

    return null;
  };

  // Handler para acessar conta do cliente
  const handleAccessClientAccount = async (subaccount) => {
    if (!canAccessClientAccount(subaccount)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Apenas Admins e Gerentes podem acessar contas de subcontas.",
      });
      return;
    }

    // Mostrar modal de confirmação com opções
    const { value: formValues } = await Swal.fire({
      title: `Acessar conta de ${subaccount.company_name}`,
      html: `
        <div class="mb-3">
          <label for="reason" class="form-label">Motivo do acesso:</label>
          <input type="text" id="reason" class="form-control" value="Suporte técnico" placeholder="Ex: Configurar automações, Resolver problema...">
        </div>
        <div class="mb-3">
          <label for="duration" class="form-label">Duração (horas):</label>
          <select id="duration" class="form-control">
            <option value="1">1 hora</option>
            <option value="2">2 horas</option>
            <option value="4" selected>4 horas</option>
            <option value="6">6 horas</option>
            <option value="8">8 horas</option>
          </select>
        </div>
        <small class="text-muted">
          ⚠️ Todas as ações serão auditadas durante este acesso.
        </small>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Acessar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3b82f6",
      preConfirm: () => {
        const reason = document.getElementById("reason").value;
        const duration = parseInt(document.getElementById("duration").value);

        if (!reason || reason.trim().length < 5) {
          Swal.showValidationMessage("Motivo deve ter pelo menos 5 caracteres");
          return false;
        }

        return { reason: reason.trim(), duration };
      },
    });

    if (!formValues) return;

    try {
      setAccessingClient(true);

      // Obter ID da organização do usuário
      const orgId = getOrganizationId();

      console.log("🔍 Debug - Organization Detection:", {
        isOrganization: isOrganization(),
        organizationInfo: getOrganizationInfo(),
        userObject: {
          organization_id: user?.organization_id,
          organization: user?.organization,
          account_context: user?.account_context,
          superuser_level: user?.superuser_level,
        },
        detectedOrgId: orgId,
      });

      if (!orgId) {
        console.error("❌ ID da organização não encontrado");
        throw new Error(`ID da organização não encontrado. 
          Dados disponíveis:
          - É organização: ${isOrganization()}
          - Info da organização: ${JSON.stringify(getOrganizationInfo())}
          - User organization_id: ${user?.organization_id}
          
          Verifique se você está logado em uma conta de organização.`);
      }

      const response = await OrganizationService.accessClientAccount(
        orgId,
        subaccount.id,
        formValues.reason,
        formValues.duration
      );

      // Salvar sessão e trocar contexto
      const success = OrganizationService.setActiveSession(response);

      if (success) {
        setActiveClientSession(OrganizationService.getCurrentSessionInfo());

        Swal.fire({
          icon: "success",
          title: "Acesso Concedido",
          html: `
            <p>Você agora está acessando a conta de <strong>${response.client_account.company_name}</strong></p>
            <p><small class="text-muted">Sessão expira em ${formValues.duration} hora(s)</small></p>
          `,
          confirmButtonText: "Continuar",
        }).then(() => {
          // Recarregar a página para aplicar o novo contexto
          window.location.href = '/'
        });
      } else {
        throw new Error("Erro ao configurar sessão");
      }
    } catch (error) {
      console.error("Erro ao acessar conta do cliente:", error);

      let errorMessage = "Não foi possível acessar a conta do cliente.";
      let showDebugInfo = false;

      // Tratamento específico baseado no tipo de erro
      if (
        error.message &&
        error.message.includes("ID da organização não encontrado")
      ) {
        errorMessage =
          "Erro de configuração: ID da organização não foi encontrado.";
        showDebugInfo = true;
      } else if (error.response?.status === 400) {
        errorMessage =
          error.response?.data?.error || "Dados inválidos fornecidos.";
      } else if (error.response?.status === 403) {
        errorMessage = "Sem permissão para acessar esta conta do cliente.";
      } else if (error.response?.status === 404) {
        errorMessage = "Conta do cliente não encontrada na sua organização.";
      } else if (error.response?.status === 409) {
        errorMessage =
          "Você já possui uma sessão ativa. Encerre-a antes de iniciar outra.";
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.name === "NetworkError" || !error.response) {
        errorMessage =
          "Erro de conectividade. Verifique sua conexão com a internet.";
      }

      // Modal de erro com opção de debug
      const errorModal = {
        icon: "error",
        title: "Erro ao Acessar Cliente",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      };

     

      Swal.fire(errorModal);
    } finally {
      setAccessingClient(false);
    }
  };

  // Handler para retornar à conta original
  const handleReturnToOriginalAccount = async () => {
    if (!activeClientSession) return;

    const result = await Swal.fire({
      title: "Encerrar Sessão",
      html: `
        <p>Deseja retornar à sua conta original?</p>
        <p><small class="text-muted">
          Você estava acessando: <strong>${activeClientSession.clientAccount?.company_name}</strong><br>
          Motivo: ${activeClientSession.reason}
        </small></p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, retornar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3b82f6",
    });

    if (!result.isConfirmed) return;

    try {
      setAccessingClient(true);

      await OrganizationService.returnToOriginalAccount(
        activeClientSession.sessionId
      );

      // Limpar sessão local
      OrganizationService.clearActiveSession();
      setActiveClientSession(null);

      Swal.fire({
        icon: "success",
        title: "Sessão Encerrada",
        text: "Você retornou à sua conta original.",
        confirmButtonText: "OK",
      }).then(() => {
        // Recarregar a página para aplicar o contexto original
        window.location.reload();
      });
    } catch (error) {
      console.error("Erro ao retornar à conta original:", error);

      let errorMessage = "Erro ao encerrar sessão.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setAccessingClient(false);
    }
  };

  /*   const handleExportSubaccounts = () => {
    // Implementar lógica de exportação
    Swal.fire({
      icon: "info",
      title: "Exportar Dados",
      text: "Funcionalidade de exportação será implementada.",
    });
  }; */

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "primary" : "outline-primary"}
          size="sm"
          className="me-1"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="d-flex justify-content-center align-items-center mt-3">
        <Button
          variant="outline-primary"
          size="sm"
          className="me-2"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Anterior
        </Button>
        {pages}
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-2"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Próximo
        </Button>
      </div>
    );
  };

  return (
    <div className="organization-subaccounts">
      {/* Banner de sessão ativa */}
      {activeClientSession && (
        <Alert
          variant="info"
          className="d-flex align-items-center justify-content-between mb-4"
        >
          <div className="d-flex align-items-center">
            <IconUserCheck size={20} className="me-2" />
            <div>
              <strong>Sessão Ativa:</strong> Acessando conta de{" "}
              <strong>{activeClientSession.clientAccount?.company_name}</strong>
              <br />
              <small>
                Motivo: {activeClientSession.reason} | Tempo restante:{" "}
                {activeClientSession.timeLeftMinutes} minutos
                {activeClientSession.isExpiringSoon && (
                  <span className="text-warning ms-1">
                    ⚠️ Expirando em breve
                  </span>
                )}
              </small>
            </div>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleReturnToOriginalAccount}
            disabled={accessingClient}
          >
            <IconLogout size={14} className="me-1" />
            Retornar
          </Button>
        </Alert>
      )}

      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <h3 className="d-flex">
              <IconUsers size={30} className="me-2" />
              Subcontas da Organização
            </h3>
            <p className="text-muted">
              Gerencie as empresas cadastradas em sua organização
            </p>
          </div>
          <div className="d-flex gap-2">
            {/*  <Button variant="outline-primary" onClick={handleExportSubaccounts}>
              <IconDownload size={16} className="me-1" />
              Exportar
            </Button> */}

            <CreateSubaccountButton />
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col xs={12} md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <IconSearch size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por empresa ou email..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <IconFilter size={16} />
                </InputGroup.Text>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                >
                  <option value="all">Todos os Status</option>
                  <option value="active">Ativas</option>
                  <option value="inactive">Inativas</option>
                </Form.Select>
              </InputGroup>
            </Col>
            <Col xs={12} md={3}>
              <div className="text-muted small">
                Total: {totalCount} subcontas
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de Subcontas */}
      <Card>
        <Card.Header className="d-flex gap-2 align-items-center">
          <IconUsers size={20} />
          <h5 className="mb-0">Lista de Subcontas</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Carregando subcontas...</p>
              </div>
            </div>
          ) : subaccounts.length > 0 ? (
            <>
              <Table responsive hover className="subaccounts-table">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>Email</th>
                    <th>Usuários</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {subaccounts.map((subaccount) => (
                    <tr key={subaccount.id}>
                      <td>
                        <div className="subaccount-name">
                          {subaccount.company_name}
                          {subaccount.brand_name && (
                            <small className="d-block text-muted">
                              {subaccount.brand_name}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="text-break">{subaccount.email}</span>
                      </td>
                      <td>
                        <span className="user-count">
                          <strong>{subaccount.current_users_count || 0}</strong>
                          /{subaccount.max_users_allowed}
                        </span>
                        <div
                          className="progress mt-1"
                          style={{ height: "4px" }}
                        >
                          <div
                            className={`progress-bar ${
                              subaccount.usagePercentage > 80
                                ? "bg-warning"
                                : subaccount.usagePercentage > 95
                                ? "bg-danger"
                                : "bg-primary"
                            }`}
                            style={{
                              width: `${subaccount.usagePercentage}%`,
                            }}
                          />
                        </div>
                        {subaccount.usagePercentage > 80 && (
                          <small className="text-warning">
                            <IconAlertTriangle size={12} className="me-1" />
                            Limite próximo
                          </small>
                        )}
                      </td>
                      <td>
                        <Badge bg={subaccount.statusVariant}>
                          {subaccount.statusText}
                        </Badge>
                      </td>
                      <td>
                        <span className="date-info">
                          {subaccount.formattedCreatedAt}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <OverlayTrigger
                            overlay={<Tooltip>Ver detalhes</Tooltip>}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleViewSubaccount(subaccount.id)
                              }
                              disabled={!canViewSubaccount(subaccount)}
                            >
                              <IconEye size={14} />
                            </Button>
                          </OverlayTrigger>

                          <OverlayTrigger
                            overlay={
                              <Tooltip>
                                {canEditSubaccount(subaccount)
                                  ? "Editar subconta"
                                  : "Sem permissão para editar"}
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                handleEditSubaccount(subaccount.id)
                              }
                              disabled={!canEditSubaccount(subaccount)}
                            >
                              <IconEdit size={14} />
                            </Button>
                          </OverlayTrigger>

                          <OverlayTrigger
                            overlay={
                              <Tooltip>
                                {canAccessClientAccount(subaccount)
                                  ? "Acessar cliente"
                                  : !user || user.superuser_level > 2
                                  ? "Apenas Admins e Gerentes podem acessar"
                                  : subaccount.status !== 1
                                  ? "Cliente deve estar ativo"
                                  : "Sem permissão"}
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() =>
                                handleAccessClientAccount(subaccount)
                              }
                              disabled={
                                !canAccessClientAccount(subaccount) ||
                                accessingClient
                              }
                            >
                              {accessingClient ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <IconUserCheck size={14} />
                              )}
                            </Button>
                          </OverlayTrigger>

                          <OverlayTrigger
                            overlay={<Tooltip>Implementar Agente</Tooltip>}
                          >
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleImplementAgent(subaccount)}
                              disabled={!canEditSubaccount(subaccount)} // Usar a mesma permissão do editar
                            >
                              <IconRocket size={14} />
                            </Button>
                          </OverlayTrigger>

                          <OverlayTrigger
                            overlay={
                              <Tooltip>
                                {canDeleteSubaccount(subaccount)
                                  ? "Excluir subconta"
                                  : "Apenas donos podem excluir"}
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteSubaccount(subaccount)}
                              disabled={!canDeleteSubaccount(subaccount)}
                            >
                              <IconTrash size={14} />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {renderPagination()}
            </>
          ) : !isOrganization() ? (
            <Alert variant="warning" className="text-center">
              <IconAlertTriangle size={24} className="mb-2" />
              <p className="mb-0">
                Esta funcionalidade está disponível apenas para organizações.
              </p>
            </Alert>
          ) : user?.superuser_level > 2 ? (
            <Alert variant="warning" className="text-center">
              <IconAlertTriangle size={24} className="mb-2" />
              <p className="mb-0">
                Você não tem permissão para visualizar subcontas.
              </p>
            </Alert>
          ) : (
            <Alert variant="info" className="text-center">
              <IconUsers size={24} className="mb-2" />
              <p className="mb-0">
                {searchTerm || statusFilter !== "all"
                  ? "Nenhuma subconta encontrada com os filtros aplicados."
                  : "Nenhuma subconta encontrada."}
              </p>
              <small>
                {searchTerm || statusFilter !== "all"
                  ? "Tente ajustar os filtros de busca."
                  : "Convide empresas para começar a usar sua organização."}
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>
      {/* Modal de edição */}
      <EditSubaccountModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        subaccountId={selectedSubaccountId}
        onSuccess={handleModalSuccess}
      />

      {/* Modal de implementação */}
      <AgentImplementationModal
        show={showImplementationModal}
        onHide={() => setShowImplementationModal(false)}
        subaccount={selectedSubaccountForImplementation}
        onSuccess={handleImplementationSuccess}
      />
    </div>
  );
};

export default OrganizationSubaccountsList;
